// Shared types, slot definitions and helpers for admin leaderboard management.

export type PlayKind = 'ALL' | 'OPEN' | 'LEAGUE';
export type RankingKind = 'INDIVIDUAL_PERFORMANCE_POINTS' | 'TEAM_SCORES';

export interface ILeaderboard {
  id: string;
  name: string;
  centerId: string | null;
  playKind: PlayKind;
  rankingKind: RankingKind;
  // For TEAM_SCORES boards: the exact number of players a team must have for a
  // game to count. null means "any team size" (legacy boards).
  teamScoresTeamSize: number | null;
  startTime: string | null;
  endTime: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// A "slot" describes one of the fixed, managed leaderboard configurations
// (e.g. the global/center "Any" board). The (playKind, rankingKind) pair is the
// identity used to group raw leaderboards returned by the API into a slot.
export interface ISlotDef {
  key: string;
  label: string;
  description: string;
  playKind: PlayKind;
  rankingKind: RankingKind;
  // Optional slots (e.g. center "League Scores") may legitimately have nothing
  // configured and are not part of the always-present trio.
  optional: boolean;
  defaultName: string;
  // Only set on TEAM_SCORES slots: the required team size (1–5) that scopes the
  // board and forms part of its slot identity.
  teamScoresTeamSize?: number | null;
}

// The "Any / Open / League" trio, ranked by individual performance points.
// Used for both global (centerId === null) and per-center management.
export const STANDARD_SLOTS: ISlotDef[] = [
  {
    key: 'all',
    label: 'All',
    description: 'All points earned (league and open play).',
    playKind: 'ALL',
    rankingKind: 'INDIVIDUAL_PERFORMANCE_POINTS',
    optional: false,
    defaultName: 'Any',
  },
  {
    key: 'open',
    label: 'Open',
    description: 'Points earned in open (non-league) play only.',
    playKind: 'OPEN',
    rankingKind: 'INDIVIDUAL_PERFORMANCE_POINTS',
    optional: false,
    defaultName: 'Open',
  },
  {
    key: 'league',
    label: 'League',
    description: 'Points earned in league play only.',
    playKind: 'LEAGUE',
    rankingKind: 'INDIVIDUAL_PERFORMANCE_POINTS',
    optional: false,
    defaultName: 'League',
  },
];

// Team-scores boards are split by the required team size. The label is the
// user-facing name for that size; the size is the value sent to the API.
export const TEAM_SIZES: Array<{ size: number; label: string }> = [
  { size: 1, label: 'League Singles Scores' },
  { size: 2, label: 'League Doubles Scores' },
  { size: 3, label: 'League Trios Scores' },
  { size: 4, label: 'League Four Man Scores' },
  { size: 5, label: 'League Five Man Scores' },
];

export const TEAM_SIZES_SHORT: Array<{ size: number; label: string }> = [
  { size: 1, label: 'Singles' },
  { size: 2, label: 'Doubles' },
  { size: 3, label: 'Trios' },
  { size: 4, label: 'Four Man' },
  { size: 5, label: 'Five Man' },
];

// Center-only optional boards: team scores across league play, one per team
// size. These replace the former single "League Footbowl Scores" board.
export const LEAGUE_SCORES_SLOTS: ISlotDef[] = TEAM_SIZES.map(
  ({ size, label }): ISlotDef => ({
    key: `league_scores_${size}`,
    label,
    description: `League footbowl scores for ${size}-player (${label.toLowerCase()}) teams.`,
    playKind: 'LEAGUE',
    rankingKind: 'TEAM_SCORES',
    optional: true,
    defaultName: label,
    teamScoresTeamSize: size,
  }),
);

export const slotsFor = (centerId: string | null): ISlotDef[] =>
  centerId ? [...STANDARD_SLOTS, ...LEAGUE_SCORES_SLOTS] : [...STANDARD_SLOTS];

// All leaderboards matching a slot's identity. For team-scores slots the
// required team size is part of that identity, so boards of a different size
// (or the legacy "any size" board) do not belong to the slot.
export const leaderboardsForSlot = (
  leaderboards: ILeaderboard[],
  slot: ISlotDef,
): ILeaderboard[] =>
  leaderboards.filter(
    (lb) =>
      lb.playKind === slot.playKind &&
      lb.rankingKind === slot.rankingKind &&
      (slot.rankingKind !== 'TEAM_SCORES' ||
        (lb.teamScoresTeamSize || null) === (slot.teamScoresTeamSize || null)),
  );

const time = (iso: string | null): number =>
  iso ? new Date(iso).getTime() : 0;

// Identify "the current" leaderboard for a slot from its candidates.
// Server enforces no single-active constraint, so we rank: active first,
// then an open-ended/future endTime, then most recently created.
export const pickCurrent = (
  candidates: ILeaderboard[],
): ILeaderboard | null => {
  if (!candidates.length) {
    return null;
  }

  const now = Date.now();
  const ongoing = (lb: ILeaderboard) => !lb.endTime || time(lb.endTime) > now;

  return candidates.slice().sort((a, b) => {
    if (a.isActive !== b.isActive) {
      return a.isActive ? -1 : 1;
    }
    if (ongoing(a) !== ongoing(b)) {
      return ongoing(a) ? -1 : 1;
    }
    return time(b.createdAt) - time(a.createdAt);
  })[0];
};

// Everything that is not the current board, newest-ended first.
export const historyFor = (
  candidates: ILeaderboard[],
  current: ILeaderboard | null,
): ILeaderboard[] =>
  candidates
    .filter((lb) => !current || lb.id !== current.id)
    .slice()
    .sort(
      (a, b) =>
        time(b.endTime) - time(a.endTime) ||
        time(b.createdAt) - time(a.createdAt),
    );

export const formatDate = (iso: string | null): string => {
  if (!iso) {
    return '—';
  }
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
};
